import { Suspense, lazy } from 'react'
import type { ReactNode } from 'react'
import { RouterProvider, createBrowserRouter, Navigate } from 'react-router-dom'
import { AppShell } from '../layouts/AppShell'
import { ProtectedRoute } from '../../features/auth/components/ProtectedRoute'
import { PublicOnlyRoute } from '../../features/auth/components/PublicOnlyRoute'

const MarketingPage = lazy(() =>
  import('../../features/marketing/routes/MarketingPage').then((module) => ({ default: module.MarketingPage })),
)
const LoginPage = lazy(() => import('../../features/auth/routes/LoginPage').then((module) => ({ default: module.LoginPage })))
const LinkedinCallbackPage = lazy(() =>
  import('../../features/auth/routes/LinkedinCallbackPage').then((module) => ({ default: module.LinkedinCallbackPage })),
)
const DashboardPage = lazy(() =>
  import('../../features/dashboard/routes/DashboardPage').then((module) => ({ default: module.DashboardPage })),
)
const ApprovalsPage = lazy(() =>
  import('../../features/approvals/routes/ApprovalsPage').then((module) => ({ default: module.ApprovalsPage })),
)
const ContentPage = lazy(() => import('../../features/content/routes/ContentPage').then((module) => ({ default: module.ContentPage })))
const NewsletterPage = lazy(() =>
  import('../../features/content/routes/NewsletterPage').then((module) => ({ default: module.NewsletterPage })),
)
const ContentCreatePage = lazy(() =>
  import('../../features/content/routes/ContentCreatePage').then((module) => ({ default: module.ContentCreatePage })),
)
const ContentDetailPage = lazy(() =>
  import('../../features/content/routes/ContentDetailPage').then((module) => ({ default: module.ContentDetailPage })),
)
const KnowledgeBasePage = lazy(() =>
  import('../../features/content/routes/KnowledgeBasePage').then((module) => ({ default: module.KnowledgeBasePage })),
)
const OnboardingCompanyPage = lazy(() =>
  import('../../features/onboarding/routes/OnboardingCompanyPage').then((module) => ({ default: module.OnboardingCompanyPage })),
)
const OnboardingChoicePage = lazy(() =>
  import('../../features/onboarding/routes/OnboardingChoicePage').then((module) => ({ default: module.OnboardingChoicePage })),
)
const OnboardingVisionPage = lazy(() =>
  import('../../features/onboarding/routes/OnboardingVisionPage').then((module) => ({ default: module.OnboardingVisionPage })),
)
const OnboardingGoalsPage = lazy(() =>
  import('../../features/onboarding/routes/OnboardingGoalsPage').then((module) => ({ default: module.OnboardingGoalsPage })),
)
const OnboardingStrategyPage = lazy(() =>
  import('../../features/onboarding/routes/OnboardingStrategyPage').then((module) => ({ default: module.OnboardingStrategyPage })),
)
const OnboardingStrategyProofPage = lazy(() =>
  import('../../features/onboarding/routes/OnboardingStrategyProofPage').then((module) => ({ default: module.OnboardingStrategyProofPage })),
)
const OnboardingAudiencePage = lazy(() =>
  import('../../features/onboarding/routes/OnboardingAudiencePage').then((module) => ({ default: module.OnboardingAudiencePage })),
)
const OnboardingTonePage = lazy(() =>
  import('../../features/onboarding/routes/OnboardingTonePage').then((module) => ({ default: module.OnboardingTonePage })),
)
const OnboardingMarketPage = lazy(() =>
  import('../../features/onboarding/routes/OnboardingMarketPage').then((module) => ({ default: module.OnboardingMarketPage })),
)
const OnboardingChannelsPage = lazy(() =>
  import('../../features/onboarding/routes/OnboardingChannelsPage').then((module) => ({ default: module.OnboardingChannelsPage })),
)
const OnboardingApprovalPage = lazy(() =>
  import('../../features/onboarding/routes/OnboardingApprovalPage').then((module) => ({ default: module.OnboardingApprovalPage })),
)
const OnboardingNewsletterSetupPage = lazy(() =>
  import('../../features/onboarding/routes/OnboardingNewsletterSetupPage').then((module) => ({ default: module.OnboardingNewsletterSetupPage })),
)
const OnboardingDesignPage = lazy(() =>
  import('../../features/onboarding/routes/OnboardingDesignPage').then((module) => ({ default: module.OnboardingDesignPage })),
)
const OnboardingReviewPage = lazy(() =>
  import('../../features/onboarding/routes/OnboardingReviewPage').then((module) => ({ default: module.OnboardingReviewPage })),
)
const OnboardingHistoryPage = lazy(() =>
  import('../../features/onboarding/routes/OnboardingHistoryPage').then((module) => ({ default: module.OnboardingHistoryPage })),
)
const OnboardingResumeRedirectPage = lazy(() =>
  import('../../features/onboarding/routes/OnboardingResumeRedirectPage').then((module) => ({ default: module.OnboardingResumeRedirectPage })),
)

function withSuspense(element: ReactNode) {
  return (
    <Suspense
      fallback={
        <section className="page-section">
          <div className="page-card">Seite wird geladen...</div>
        </section>
      }
    >
      {element}
    </Suspense>
  )
}

const router = createBrowserRouter([
  {
    path: '/',
    element: withSuspense(<MarketingPage />),
  },
  {
    path: '/login',
    element: <PublicOnlyRoute />,
    children: [{ index: true, element: withSuspense(<LoginPage />) }],
  },
  {
    path: '/app',
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppShell />,
        children: [
          {
            index: true,
            element: <Navigate to="/app/dashboard" replace />,
          },
          {
            path: 'dashboard',
            element: withSuspense(<DashboardPage />),
          },
          {
            path: 'integrations/linkedin/callback',
            element: withSuspense(<LinkedinCallbackPage />),
          },
          {
            path: 'approvals',
            element: withSuspense(<ApprovalsPage />),
          },
          {
            path: 'content',
            element: withSuspense(<ContentPage />),
          },
          {
            path: 'newsletter',
            element: withSuspense(<NewsletterPage />),
          },
          {
            path: 'content/new',
            element: withSuspense(<ContentCreatePage />),
          },
          {
            path: 'content/:contentId',
            element: withSuspense(<ContentDetailPage />),
          },
          {
            path: 'knowledge-base',
            element: withSuspense(<KnowledgeBasePage />),
          },
          {
            path: 'onboarding',
            element: withSuspense(<OnboardingResumeRedirectPage />),
          },
          {
            path: 'onboarding/start',
            element: withSuspense(<OnboardingChoicePage />),
          },
          {
            path: 'onboarding/history',
            element: withSuspense(<OnboardingHistoryPage />),
          },
          {
            path: 'onboarding/company',
            element: withSuspense(<OnboardingCompanyPage />),
          },
          {
            path: 'onboarding/strategy',
            element: withSuspense(<OnboardingStrategyPage />),
          },
          {
            path: 'onboarding/strategy-proof',
            element: withSuspense(<OnboardingStrategyProofPage />),
          },
          {
            path: 'onboarding/vision',
            element: withSuspense(<OnboardingVisionPage />),
          },
          {
            path: 'onboarding/goals',
            element: withSuspense(<OnboardingGoalsPage />),
          },
          {
            path: 'onboarding/audience',
            element: withSuspense(<OnboardingAudiencePage />),
          },
          {
            path: 'onboarding/tone',
            element: withSuspense(<OnboardingTonePage />),
          },
          {
            path: 'onboarding/market',
            element: withSuspense(<OnboardingMarketPage />),
          },
          {
            path: 'onboarding/channels',
            element: withSuspense(<OnboardingChannelsPage />),
          },
          {
            path: 'onboarding/approval',
            element: withSuspense(<OnboardingApprovalPage />),
          },
          {
            path: 'onboarding/newsletter',
            element: withSuspense(<OnboardingNewsletterSetupPage />),
          },
          {
            path: 'onboarding/design',
            element: withSuspense(<OnboardingDesignPage />),
          },
          {
            path: 'onboarding/review',
            element: withSuspense(<OnboardingReviewPage />),
          },
        ],
      },
    ],
  },
])

export function AppRouter() {
  return <RouterProvider router={router} />
}